import React from 'react'
import { v4 as uuidv4 } from 'uuid'
import { withRouter } from 'react-router'
import getStrings from 'getStrings'
import { updateIntegrations } from "../../Uluweb"
import { loginWithEmailAddress, sendAccountRecoveryEmail, executeAddOrUpdateUser } from "../../Playfab"
import getUrlParameters from 'getUrlParameters'
import ReactGA from "react-ga4"
import getErrorMessage from 'getErrorMessage'
import AppContext from 'core/appContext'
import { Cookies, defaultCookieConsentName, getCookieConsentValue, resetCookieConsentValue, SAME_SITE_OPTIONS } from 'react-cookie-consent'

const DASHBOARD_PAGE_PATH = '/dashboard'

const FORMS = {
    SIGNIN: 'signin',
    FORGOT_PASSWORD: 'forgotPassword'
}
  

class ParentPortalSignInForm extends React.Component {

    constructor(props) {
        super(props)

        this.state = {
            form: FORMS.SIGNIN,
            cookieConsent: getCookieConsentValue(),
        }
    }

    signInClicked = async (event) => {
        event.preventDefault()

        if (!document.forms[event.target.dataset.form].reportValidity()) return        // EXIT

        const callerFormName = event.target.dataset.form
        const formData = this.getFormDataJSON(callerFormName)

        if (!formData.Email || !formData.Password || !getCookieConsentValue()) { // It should only get on browsers that don't support .reportValidity() (Mobile Firefox)
            alert('All fields are required')
            return  // EXIT
        }
        
        this.blockUI(true)

        const playfabAPIResponse = await this.tryLoginWithEmailAddress(formData.Email, formData.Password)
        if (!playfabAPIResponse) // Playfab returned an error and has already shown the error message to the client
            return  // EXIT

        if (playfabAPIResponse.status !== 200) {
            // this is the only error returned by PlayFab on this call: https://docs.microsoft.com/en-us/rest/api/playfab/client/authentication/login-with-email-address?view=playfab-rest
            this.displayError('InvalidEmailOrPassword')
        } else {
            const uluwebToken = uuidv4()

            try {

                const playfabID = playfabAPIResponse.data.data.PlayFabId
                const entityToken = playfabAPIResponse.data.data.EntityToken.EntityToken
                const updateUserResponse = await this.tryUpdateUser(entityToken, uluwebToken, playfabID)

                if (!updateUserResponse?.success) {
                    this.blockUI(false)
                    return
                }
            } catch (err) {
                console.error(err)
                this.displayError('GENERIC_ERROR')
                this.blockUI(false)
                return
            }

            const sessionObject = {
                ...playfabAPIResponse.data,
                token: uluwebToken,
                email: formData.Email
            }

            localStorage.setItem('session', JSON.stringify(sessionObject))
            ReactGA.event('sign_in', sessionObject)

            await updateIntegrations(uluwebToken)

            this.props.history.push(`${DASHBOARD_PAGE_PATH}?${getUrlParameters()}`)
        }
        
        this.blockUI(false)
    }

    tryUpdateUser = async (entityToken, uluwebToken, playfabID) => {
        try {
            const response = await executeAddOrUpdateUser(entityToken, uluwebToken, playfabID, false)

            if (!response.data.data.FunctionResult.success)
                this.displayError(response.data.data.FunctionResult.error)
            else if (!response.data.data.FunctionResult.userAPIResponse.success)
                this.displayError(response.data.data.FunctionResult.userAPIResponse.error)
            else
                return response.data.data.FunctionResult.userAPIResponse
        } catch (err) {
            console.error(err)
            this.displayError(err)
        }
    }

    tryLoginWithEmailAddress = async (email, password) => {
        try {
            return await loginWithEmailAddress(email, password)
        } catch (err) {
            this.blockUI(false)
            this.displayError(err.response.data.error)
        }
    }

    getFormDataJSON = (formName) => Object.fromEntries(new FormData(document.forms[formName]).entries())
    
    forgottenPasswordClicked = async(event) => {
        event.preventDefault()
        const formData = this.getFormDataJSON('formForgottenPassword')
        const playfabAPIResponse = await this.trySendAccountRecoveryEmail(formData.Email)
        if (playfabAPIResponse && playfabAPIResponse.status === 200) {
            const strings = getStrings()
            alert(strings.PARENT_PORTAL_ALERT_RESET_PASSWORD_SUCCESS)
            this.setState({form: FORMS.SIGNIN})
        }
    }

    trySendAccountRecoveryEmail = async (email) => {
        try {
            return await sendAccountRecoveryEmail(email)
        } catch (err) {
            this.blockUI(false)
            this.displayError(err.response.data.error)
        }
    }

    blockUI = (shouldBlock) => this.context.setBlockUI(shouldBlock)

    displayError = (errorCode) => {
        const strings = getStrings()
        if (!errorCode || Object.keys(errorCode).length === 0) {
            console.error('signin.displayError: generic: ', errorCode)
            return alert(strings.PARENT_PORTAL_MANAGE_ACCOUNT_ERROR_GENERIC_ERROR)
        }

        console.error(`signin.displayError: ${errorCode}`)
        alert(getErrorMessage('PARENT_PORTAL_SIGN_IN_FORM_ERROR_', errorCode, strings))
    }

    setCookieConsent = (value) => {
        if (!value) {
            resetCookieConsentValue()
        }
        else {
            const cookieSecurity = window.location ? window.location.protocol === 'https:' : true
            Cookies.set(defaultCookieConsentName, 'true', { expires: 365, sameSite: SAME_SITE_OPTIONS.LAX, secure: cookieSecurity })
        }
    }

    render() {
        return (
            <>
                {this.state.form === FORMS.SIGNIN && this.renderSignInForm()}
                {this.state.form === FORMS.FORGOT_PASSWORD && this.renderForgottenPasswordForm()}
            </>
        )
    }

    renderSignInForm() {
        const strings = getStrings()

        return (
            <>
                <h1 className='text-center text-blue margin-bottom-5'>{strings.PARENT_PORTAL_SIGN_IN_FORM_H2}</h1>
                <div className='form-box'>
                    <p><strong>{strings.PARENT_PORTAL_SIGN_IN_FORM_CREATE_ACCOUNT_P}{' '}
                        <a href={`/signup?${getUrlParameters()}`}>{strings.PARENT_PORTAL_SIGN_IN_FORM_CREATE_ACCOUNT_LINK}</a></strong>
                    </p>
                    <form id='formSignIn'>
                        <input name='Email' type='email' placeholder={strings.PARENT_PORTAL_FORM_EMAIL} required className='margin-bottom-2' />
                        <input name='Password' type='password' placeholder={strings.PARENT_PORTAL_SIGN_IN_FORM_PASSWORD} required className='margin-bottom-2' />
                        { !this.state.cookieConsent && (
                            <label className='flex-row small-text margin-bottom-2'>
                                <input name="Cookies" type='checkbox' required className='margin-right-2' onChange={(event) => this.setCookieConsent(event.target.checked)} />
                                <span>{strings.PARENT_PORTAL_SIGN_UP_FORM_COOKIE_CONSENT_P}</span>
                            </label>
                        ) }
                        <p className='margin-bottom-2 text-right'>
                            <button className='link text-blue' type='button' onClick={() => this.setState({form: FORMS.FORGOT_PASSWORD})}>{strings.PARENT_PORTAL_SIGN_IN_FORM_FORGOT_PASSWORD_LINK}</button> 
                        </p>
                        <button 
                            data-form='formSignIn'
                            onClick={this.signInClicked.bind(this)}
                            className="form-button"
                            type='button'
                        >
                            {strings.PARENT_PORTAL_SIGN_IN_FORM_BUTTON}
                        </button>
                    </form>
                </div>
            </>
        )
    }

    renderForgottenPasswordForm() {
        const strings = getStrings()

        return (
            <>
                <h1 className='text-center text-blue'>{strings.PARENT_PORTAL_FORGOTTEN_PASSWORD_FORM_H2}</h1>
                <p className='text-center'>{strings.PARENT_PORTAL_FORGOTTEN_PASSWORD_FORM_P}</p>
                <div className='form-box'>
                    <p><strong>{strings.PARENT_PORTAL_FORGOTTEN_PASSWORD_FORM_P_2}</strong></p>
                    <form id='formForgottenPassword'>
                        <input id='emailForgottenPasswordForm' name='Email' type='email' placeholder={strings.PARENT_PORTAL_FORM_EMAIL} required className='margin-bottom-2' />
                        <button
                            data-form='formForgottenPassword'
                            onClick={this.forgottenPasswordClicked.bind(this)}
                            className='form-button'
                        >
                            {strings.PARENT_PORTAL_FORGOTTEN_PASSWORD_FORM_BUTTON}
                        </button>
                    </form>
                </div>
            </>
        )
    }
}

ParentPortalSignInForm.contextType = AppContext

export default withRouter(ParentPortalSignInForm)